import ApprovedStoryActions from '../../components/ApprovedStoryActions/ApprovedStoryActions';
import { isSharedPlayback } from '../../api/libraryPlayback';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RenderDetail, RenderListItem, fetchRender } from '../../api/rendersApi';
import { isAbortError, toApiError } from '../../api/http';
import { getKnownCacheEpoch } from '../../api/cacheEpochApi';
import { markOfflineCacheConfirmed, isOfflineCacheCurrent, offlineCacheEntriesForPair } from '../../api/offlineCache';
import StoryWorlds from '../../components/StoryWorlds/StoryWorlds';
import StorySearch from '../../components/StoryWorlds/StorySearch';
import StoryWorldTile from '../../components/StoryWorlds/StoryWorldTile';
import StoryArtwork from '../../components/StoryArtwork/StoryArtwork';
import { groupStoryWorlds } from '../../components/StoryWorlds/storyWorldModel';
import BedtimePairChip from '../../components/BedtimeContext/BedtimePairChip';
import { EmptyState, PillButton, Shelf, StateMessage } from '../../components/ui';
import { SearchIcon } from '../../components/icons';
import { ApiError } from '../../api/http';
import { useParentPreview } from '../../context/ParentPreviewContext';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { Story, useApp } from '../../context/AppContext';
import { formatDurationLabel } from '../../utils/duration';
import { fetchApprovedForPair } from './approvedRenders';
import './MyStories.css';
import GiftShelf from './GiftShelf';
import { sharedReviewEnabled } from '../../audio/personalMessagesApi';

function cacheApprovedAudio(detail: RenderDetail, pair: { householdId: string; listenerId: string; voiceId: string }, userId: string): void {
  if (!detail.audio_url || !('serviceWorker' in navigator)) return;
  const cacheEpoch = getKnownCacheEpoch(pair.householdId);
  void navigator.serviceWorker.ready.then((registration) => registration.active?.postMessage({
    type: 'CACHE_APPROVED_AUDIO',
    authorization: 'Bearer ' + localStorage.getItem('auth_token'),
    userId,
    url: detail.audio_url,
    renderId: detail.render_id,
    listenerId: pair.listenerId,
    householdId: pair.householdId,
    voiceId: pair.voiceId,
    storyId: detail.story_id,
    cacheEpoch,
    accessExpiresAt: detail.offline_access_expires_at,
  })).catch(() => undefined);
}

const ApprovedStories: React.FC = () => {
  const { state, fetchStories } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const context = useBedtimeContext();
  const pair = context.pair;
  const { playError, playErrorRenderId, handlePlay } = useParentPreview()!;
  const loadRequestRef = useRef(0);
  const [approved, setApproved] = useState<RenderListItem[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'success' | 'empty' | 'error'>('loading');
  const [error, setError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [offlineReady, setOfflineReady] = useState<Set<string>>(new Set());
  const [networkOnline, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const online = networkOnline && !state.offlineSession;
  const [loadedPairKey, setLoadedPairKey] = useState<string | null>(null);
  const [params] = useSearchParams();
  const storyModeState = params.get('storyMode');
  const requestedStory = params.get('story');
  const [searchOpen, setSearchOpen] = useState(false);
  const currentPairKey = pair ? `${pair.householdId}:${pair.listenerId}:${pair.voiceId}` : '';

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  const load = useCallback(async (signal?: AbortSignal, background = false) => {
    if (!pair) return;
    const requestId = ++loadRequestRef.current;
    if (!background) { setLoadState('loading'); setLoadedPairKey(null); }
    setError(null);
    try {
      const exact = await fetchApprovedForPair(pair, signal);
      if (requestId !== loadRequestRef.current) return;
      setApproved(exact);
      setLoadedPairKey(currentPairKey);
      setLoadState(exact.length ? 'success' : 'empty');
      // The SW receives only server-filtered, exact-pair approved details.
      // Yield to the first scene, then fetch at most two details at once.
      // Large audio transfers are serialized by the worker itself.
      void (async () => {
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        const pending = exact.filter(row => !isSharedPlayback(row.render_id));
        const worker = async () => {
          while (pending.length && requestId === loadRequestRef.current && !signal?.aborted) {
            const render = pending.shift()!;
            try {
              const detail = await fetchRender(render.render_id, true, signal);
              if (requestId === loadRequestRef.current && !signal?.aborted && detail.status === 'parent_approved' && detail.household_id === pair.householdId && detail.listener_id === pair.listenerId && detail.voice_id === pair.voiceId && state.user?.id) cacheApprovedAudio(detail, pair, state.user.id);
            } catch { /* Background saving must not block browsing. */ }
          }
        };
        await Promise.all([worker(), worker()]);
      })();
    } catch (requestError) {
      if (!isAbortError(requestError) && requestId === loadRequestRef.current) {
        const safeError = toApiError(requestError);
        const cached = (safeError.code === 'offline' || (state.offlineSession && safeError.status === null))
          ? offlineCacheEntriesForPair(state.user?.id || '', pair, getKnownCacheEpoch(pair.householdId))
          : [];
        if (cached.length) {
          setApproved(cached.map((entry) => ({
            render_id: entry.renderId,
            story_id: entry.storyId || '',
            voice_id: entry.voiceId,
            household_id: entry.householdId,
            child_slug: null,
            listener_id: entry.listenerId,
            listener_name: null,
            status: 'parent_approved',
            created_at: entry.confirmedAt,
          })));
          setLoadedPairKey(currentPairKey);
          setLoadState('success');
        } else {
          setError(safeError); setLoadedPairKey(currentPairKey); setLoadState('error');
        }
      }
    }
  }, [currentPairKey, pair, state.offlineSession, state.user?.id]);

  useEffect(() => { void fetchStories(); }, [fetchStories]);
  useEffect(() => {
    if (!pair || context.status === 'loading' || context.status === 'needs_selection' || context.status === 'error' || context.status === 'missing_bookends') return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); loadRequestRef.current += 1; };
  }, [context.status, load, pair]);

  useEffect(() => {
    // The worker only caches same-origin production API responses.
    if (!navigator.serviceWorker || new URL(process.env.REACT_APP_API_URL ?? 'http://localhost:8000', window.location.href).origin !== window.location.origin) return;
    const onController = () => { if (context.status === 'ready') void load(undefined, true); };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onController);
  }, [context.status, load]);

  useEffect(() => {
    setApproved([]);
    setLoadedPairKey(null);
    setOfflineReady(new Set());
  }, [pair?.householdId, pair?.listenerId, pair?.voiceId]);

  useEffect(() => () => {
    loadRequestRef.current += 1;
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type !== 'APPROVED_AUDIO_CACHED' || !data.renderId || !pair || data.householdId !== pair.householdId || data.listenerId !== pair.listenerId || data.cacheEpoch == null) return;
      if (!state.user?.id || data.userId !== state.user.id || data.voiceId !== pair.voiceId || data.cacheEpoch !== getKnownCacheEpoch(pair.householdId)) return;
      markOfflineCacheConfirmed(state.user.id, { renderId: data.renderId, storyId: data.storyId, householdId: pair.householdId, listenerId: pair.listenerId, voiceId: data.voiceId || pair.voiceId, cacheEpoch: data.cacheEpoch, confirmedAt: new Date().toISOString(), accessExpiresAt: data.accessExpiresAt });
      setOfflineReady((previous) => new Set(previous).add(data.renderId));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [pair, state.user?.id]);

  const titleFor = (storyId: string) => state.stories.find((story) => story.story_id === storyId)?.title || storyId || 'Saved bedtime story';
  const voiceNameFor = (voiceId: string) => context.voices.find((voice) => voice.voice_id === voiceId)?.voice_name || 'Family voice';

  const listenerName = context.listeners.find((listener) => listener.listener_id === pair?.listenerId)?.display_name;
  const canStartStoryMode = context.status === 'ready';
  const pairLoaded = loadedPairKey === currentPairKey;
  const byStory = new Map(approved.map((render) => [render.story_id, render]));
  const libraryStories: Story[] = approved.map((render) => state.stories.find((story) => story.story_id === render.story_id) || {
    story_id: render.story_id || render.render_id,
    title: titleFor(render.story_id), description: 'An approved story saved for your bedtime.', age_range: '', tags: [],
    estimated_duration_minutes: render.estimated_duration_minutes,
  });
  // Offline legacy rows can lack story_id; keep their stable render identity.
  approved.forEach((render) => { if (!render.story_id) byStory.set(render.render_id, render); });
  const isSavedOffline = (render: RenderListItem) => offlineReady.has(render.render_id) || isOfflineCacheCurrent(state.user?.id || '', render.render_id, pair?.householdId || '', pair?.listenerId || '', pair?.voiceId || '', getKnownCacheEpoch(pair?.householdId || ''));

  const prerequisite = () => {
    switch (context.status) {
      case 'needs_selection':
        return <EmptyState headingLevel={2} title="Choose bedtime" message="Pick who is listening and the Family voice they will hear." action={{ label: 'Choose bedtime', onClick: () => context.openPairDialog() }} />;
      case 'no_listener':
        return <EmptyState headingLevel={2} title="Add a listener first" message="Luma uses a listener to keep stories and goodnights personal." action={{ label: 'Add a listener', to: '/account' }} />;
      case 'no_ready_voice':
        return <EmptyState headingLevel={2} title="Add a family voice" message="Add a family voice before making a story." action={{ label: 'Add a family voice', to: '/voice' }} />;
      case 'missing_bookends':
        return pair ? <EmptyState headingLevel={2} title={`Add ${listenerName || 'this listener'}’s hello and goodnight`} message="Save the three personal messages before making or playing stories for this listener." action={{ label: 'Record personal messages', to: `/voice/${pair.voiceId}/personalize/${pair.listenerId}` }} /> : null;
      case 'error':
        return <StateMessage kind="error" error={context.error as ApiError} retry={{ onClick: () => void context.refresh() }} />;
      default:
        return null;
    }
  };

  const shelfState = () => {
    if (context.status !== 'ready' || !pair) return null;
    if (!pairLoaded) return <StateMessage kind="loading" title="Checking your saved stories…" shape="shelf" testId="my-stories-loading" />;
    if (loadState === 'error' && error?.code === 'offline') return <StateMessage kind="error" title="These stories aren’t saved on this device" message="Connect once to load an approved story for bedtime." retry={{ onClick: () => void load() }} testId="my-stories-offline-error" />;
    if (loadState === 'error') return <StateMessage kind="error" error={error} retry={{ onClick: () => void load() }} testId="my-stories-error" />;
    if (loadState === 'empty') {
      return online
        ? <EmptyState headingLevel={2} title="Your bedtime shelf starts here" message="Listen and approve a story in Tonight to add it here." action={{ label: 'Open Tonight', to: '/renders' }} id="my-stories-empty" />
        : <EmptyState headingLevel={2} title="These stories aren’t saved on this device" message="Connect once to load an approved story for bedtime." action={{ label: 'Try again', onClick: () => void load() }} id="my-stories-empty" />;
    }
    return null;
  };

  const libraryWorlds = groupStoryWorlds(libraryStories);
  const shelfTiles = libraryWorlds.map((world) => {
    const first = world.chapters[0];
    const render = byStory.get(first.story_id)!;
    // Two caption lines: the voice (or chapter count for a book), then the
    // offline status when the audio is confirmed saved on this device.
    const saved = isSavedOffline(render);
    const firstLine = world.serialized ? `${world.chapters.length} chapters available` : voiceNameFor(render.voice_id);
    const metadata = saved ? [firstLine, 'Saved for bedtime'] : world.serialized ? [firstLine, voiceNameFor(render.voice_id)] : [firstLine];
    return {
      world,
      to: `/my-stories?story=${encodeURIComponent(first.story_id)}`,
      duration: formatDurationLabel(first.estimated_duration_minutes ?? render.estimated_duration_minutes, render.output_seconds),
      metadata,
    };
  });

  const openStory = (story: Story) => {
    setSearchOpen(false);
    const next = new URLSearchParams(location.search);
    next.set('story', story.story_id);
    navigate({ search: next.toString() });
  };

  const showImmersive = Boolean(requestedStory) && context.status === 'ready' && pair && pairLoaded && loadState === 'success';

  if (showImmersive && pair) {
    return <div className="my-stories my-stories--immersive luma-immersive">
      <StoryWorlds key={currentPairKey} back={{ to: '/my-stories', label: 'Back to My stories' }} stories={libraryStories} library title="My stories" subtitle={`Parent-approved stories for ${listenerName || 'tonight'}.`} storageKey={`luma:worlds:${state.user?.id || 'anonymous'}:${currentPairKey}`}
        duration={(story) => { const render = byStory.get(story.story_id)!; return formatDurationLabel(story.estimated_duration_minutes ?? render.estimated_duration_minutes, render.output_seconds); }}
        status={(story) => { const render = byStory.get(story.story_id)!; return <><span>{voiceNameFor(render.voice_id)}</span>{isSavedOffline(render) && <span data-testid="offline-badge">Saved for bedtime</span>}</>; }}
        action={(story) => <ApprovedStoryActions story={byStory.get(story.story_id)!} title={story.title} />} />
      {playError && playErrorRenderId && <StateMessage kind="error" error={playError} title="This story could not be opened" retry={{ onClick: () => void handlePlay(playErrorRenderId) }} />}

    </div>;
  }

  return <div className="my-stories">
    <div className="my-stories__head">
      <h1>My stories</h1>
      {canStartStoryMode && pairLoaded && loadState === 'success' && <PillButton variant="primary" to="/story-time/setup">Start story time</PillButton>}
    </div>
    {pair && listenerName && context.status === 'ready' && <p className="my-stories__subtitle">Parent-approved stories for {listenerName}.</p>}
    <BedtimePairChip />
    {storyModeState === 'unavailable' && <StateMessage kind="error" title="Story Mode couldn’t start" message="Try loading the story again before handing over the device." retry={{ onClick: () => navigate('/story-time/setup', { replace: true }) }} testId="my-stories-story-mode-unavailable" />}
    {prerequisite()}
    <GiftShelf />
    {shelfState()}
    {context.status === 'ready' && pair && pairLoaded && loadState === 'success' && <>
      <Shelf
        id="my-stories-ready"
        title="Ready for bedtime"
        items={shelfTiles}
        getKey={(tile) => tile.world.id}
        renderItem={(tile, index) => <StoryWorldTile world={tile.world} to={tile.to} duration={tile.duration} metadata={tile.metadata} loading={index < 4 ? 'eager' : 'lazy'} />}
      />
      <div className="my-stories__tools">
        <PillButton variant="secondary" size="compact" icon={<SearchIcon size={20} />} onClick={() => setSearchOpen(true)} aria-haspopup="dialog">Search your stories</PillButton>
        <Link className="my-stories__discover" to="/discover">Discover another story</Link>
      </div>
      {online && (
        // One online visit keeps the story view's hero artwork available
        // offline: request the same viewport-sized variant the immersive view
        // will use, so the worker's public artwork cache already holds it.
        <div className="my-stories__warm" aria-hidden="true" data-testid="my-stories-artwork-warm">
          {libraryStories.filter((story) => story.thumbnail_url).slice(0, 12).map((story) => (
            <StoryArtwork key={story.story_id} src={story.thumbnail_url} srcSet={story.thumbnail_srcset} sizes="100vw" loading="eager" imageClassName="my-stories__warm-image" fallbackClassName="my-stories__warm-image" fallbackSize={1} />
          ))}
        </div>
      )}
      {searchOpen && <StorySearch library stories={libraryStories} onClose={() => setSearchOpen(false)} onSelect={openStory} />}
    </>}
  </div>;
};

const MyStories: React.FC = () => <>
  {sharedReviewEnabled && <PillButton to="/household-stories" variant="secondary">Stories for your family</PillButton>}
  <ApprovedStories />
</>;

export default MyStories;
